/**
 * Ops Estimate Pricing panel — decision-support drawer for quote create/edit.
 * Works without quote.automatedPreparation. Does not auto-commit quote totals.
 * Unresolved geography: Google/AI candidates → persist to lead → re-estimate.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  applyLeadAddressResolution,
  estimatePricingFromLead,
  resolveLeadAddresses,
} from "../services/estimatePricingService.js";
import { formatQuoteMoney } from "../services/quoteOperationalStatus.js";
import { mapOpsEstimateToBreakdown } from "../utils/mapOpsEstimateToBreakdown.js";
import { SA_PROVINCE_OPTIONS } from "@/features/partners/constants/providerProfileOptions.js";
import SharedRouteTariffDrawer from "./SharedRouteTariffDrawer.jsx";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|null} props.leadId
 * @param {string|null} [props.quoteId]
 * @param {{
 *   from?: string,
 *   to?: string,
 *   serviceType?: string,
 *   items?: unknown,
 *   itemsList?: unknown,
 * }} [props.formSnapshot]
 */
const EstimatePricingPanel = ({
  open,
  onClose,
  leadId = null,
  quoteId = null,
  formSnapshot = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resolveData, setResolveData] = useState(null);
  const [pickupCandidateId, setPickupCandidateId] = useState("");
  const [deliveryCandidateId, setDeliveryCandidateId] = useState("");
  const [pickupProvince, setPickupProvince] = useState("");
  const [deliveryProvince, setDeliveryProvince] = useState("");
  const [breakdownExpanded, setBreakdownExpanded] = useState(true);
  const [routeKmOverride, setRouteKmOverride] = useState("");
  const [collectionKmOverride, setCollectionKmOverride] = useState("");
  const [deliveryKmOverride, setDeliveryKmOverride] = useState("");
  const [tariffDrawerOpen, setTariffDrawerOpen] = useState(false);

  const from = formSnapshot?.from;
  const to = formSnapshot?.to;
  const serviceType = formSnapshot?.serviceType;
  const items = formSnapshot?.items;
  const itemsList = formSnapshot?.itemsList;

  const resetResolveUi = useCallback(() => {
    setResolveData(null);
    setPickupCandidateId("");
    setDeliveryCandidateId("");
    setPickupProvince("");
    setDeliveryProvince("");
  }, []);

  const runEstimate = useCallback(
    async ({
      forceRefresh = false,
      routeKm,
      collectionKm,
      deliveryKm,
    } = {}) => {
      if (!leadId) {
        setError("Link a lead first to estimate pricing.");
        setResult(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Confirmable km are only sent when the caller passes them explicitly
        // (Save & re-estimate). Refresh / initial open must not persist
        // suggested-only values as ops_manual — and empty strings must not
        // coerce via Number("") === 0.
        const confirmingRouteKm = routeKm !== undefined;
        const confirmingCollectionKm = collectionKm !== undefined;
        const confirmingDeliveryKm = deliveryKm !== undefined;
        const routeKmRaw = confirmingRouteKm ? routeKm : "";
        const collectionRaw = confirmingCollectionKm ? collectionKm : "";
        const deliveryRaw = confirmingDeliveryKm ? deliveryKm : "";
        const routeKmText = String(routeKmRaw ?? "").trim();
        const collectionText = String(collectionRaw ?? "").trim();
        const deliveryText = String(deliveryRaw ?? "").trim();
        const kmNum = routeKmText === "" ? NaN : Number(routeKmText);
        const collectionNum =
          collectionText === "" ? NaN : Number(collectionText);
        const deliveryNum = deliveryText === "" ? NaN : Number(deliveryText);
        const data = await estimatePricingFromLead({
          leadId,
          quoteId: quoteId || undefined,
          forceRefresh: forceRefresh === true,
          formSnapshot: {
            from,
            to,
            serviceType,
            items,
            itemsList,
            ...(confirmingRouteKm && Number.isFinite(kmNum) && kmNum > 0
              ? { routeKm: kmNum }
              : {}),
            ...(confirmingCollectionKm &&
            Number.isFinite(collectionNum) &&
            collectionNum >= 0
              ? { originDepotToPickupKm: collectionNum }
              : {}),
            ...(confirmingDeliveryKm &&
            Number.isFinite(deliveryNum) &&
            deliveryNum >= 0
              ? { destDepotToDropKm: deliveryNum }
              : {}),
          },
        });
        setResult(data);
        if (data?.status === "ok") {
          resetResolveUi();
          if (confirmingRouteKm && Number.isFinite(kmNum) && kmNum > 0) {
            setRouteKmOverride(String(kmNum));
          } else if (
            data?.routeKm != null &&
            Number.isFinite(Number(data.routeKm))
          ) {
            setRouteKmOverride(String(data.routeKm));
          }
        } else if (
          data?.status === "needs_admin_review" &&
          data?.reason === "route_distance_unavailable"
        ) {
          const suggested = Number(data?.suggestedRouteKm);
          if (Number.isFinite(suggested) && suggested > 0) {
            setRouteKmOverride((prev) =>
              String(prev || "").trim() ? prev : String(suggested)
            );
          }
        }

        // Prefill depot legs from confirmed / suggested values (shared CAPACITY).
        const nextCollection =
          data?.originDepotToPickupKm ??
          data?.estimate?.originDepotToPickupKm ??
          data?.suggestedCollectionKm;
        const nextDelivery =
          data?.destDepotToDropKm ??
          data?.estimate?.destDepotToDropKm ??
          data?.suggestedDeliveryKm;
        if (nextCollection != null && Number.isFinite(Number(nextCollection))) {
          setCollectionKmOverride((prev) =>
            String(prev || "").trim() ? prev : String(nextCollection)
          );
        }
        if (nextDelivery != null && Number.isFinite(Number(nextDelivery))) {
          setDeliveryKmOverride((prev) =>
            String(prev || "").trim() ? prev : String(nextDelivery)
          );
        }
        if (
          confirmingCollectionKm &&
          Number.isFinite(collectionNum) &&
          collectionNum >= 0
        ) {
          setCollectionKmOverride(String(collectionNum));
        }
        if (
          confirmingDeliveryKm &&
          Number.isFinite(deliveryNum) &&
          deliveryNum >= 0
        ) {
          setDeliveryKmOverride(String(deliveryNum));
        }
      } catch (err) {
        setResult(null);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to estimate pricing"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      leadId,
      quoteId,
      from,
      to,
      serviceType,
      items,
      itemsList,
      resetResolveUi,
    ]
  );

  useEffect(() => {
    if (!open) return undefined;
    resetResolveUi();
    setRouteKmOverride("");
    setCollectionKmOverride("");
    setDeliveryKmOverride("");
    // Pass empty overrides so a prior value from another lead is not reused
    // (setState is async; runEstimate would otherwise close over the old value).
    runEstimate({
      forceRefresh: false,
      routeKm: "",
      collectionKm: "",
      deliveryKm: "",
    });
    return undefined;
    // Intentionally only re-run when the drawer opens or lead/quote identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, quoteId]);

  useEffect(() => {
    if (!open) setTariffDrawerOpen(false);
  }, [open]);

  const handleResolveAddresses = useCallback(async () => {
    if (!leadId) return;
    setResolving(true);
    setError(null);
    try {
      const data = await resolveLeadAddresses({ leadId });
      setResolveData(data || {});
      const pickupFirst = data?.pickup?.candidates?.[0]?.id || "";
      const deliveryFirst = data?.delivery?.candidates?.[0]?.id || "";
      setPickupCandidateId(pickupFirst);
      setDeliveryCandidateId(deliveryFirst);
      setPickupProvince(data?.pickup?.candidates?.[0]?.provinceCode || "");
      setDeliveryProvince(data?.delivery?.candidates?.[0]?.provinceCode || "");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to resolve addresses"
      );
    } finally {
      setResolving(false);
    }
  }, [leadId]);

  const handleApplyAndReestimate = useCallback(async () => {
    if (!leadId) return;
    setApplying(true);
    setError(null);
    try {
      const pickupList = resolveData?.pickup?.candidates || [];
      const deliveryList = resolveData?.delivery?.candidates || [];
      const pickupCandidate =
        pickupList.find((c) => c.id === pickupCandidateId) || null;
      const deliveryCandidate =
        deliveryList.find((c) => c.id === deliveryCandidateId) || null;

      const selections = {};
      if (pickupCandidate || pickupProvince) {
        selections.pickup = {
          candidate: pickupCandidate || undefined,
          provinceCode: pickupProvince || pickupCandidate?.provinceCode,
        };
      }
      if (deliveryCandidate || deliveryProvince) {
        selections.delivery = {
          candidate: deliveryCandidate || undefined,
          provinceCode: deliveryProvince || deliveryCandidate?.provinceCode,
        };
      }
      if (!selections.pickup && !selections.delivery) {
        setError("Select at least one address candidate or province.");
        setApplying(false);
        return;
      }

      await applyLeadAddressResolution({ leadId, selections });
      await runEstimate({ forceRefresh: true });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to apply address resolution"
      );
    } finally {
      setApplying(false);
    }
  }, [
    leadId,
    pickupCandidateId,
    deliveryCandidateId,
    pickupProvince,
    deliveryProvince,
    resolveData,
    runEstimate,
  ]);

  const breakdown = mapOpsEstimateToBreakdown(result);
  const needsReview = result?.status === "needs_admin_review";
  const suggestedTotal = breakdown?.total ?? result?.estimate?.suggestedTotal;
  const showGeographyResolve =
    needsReview && result?.reason === "unresolved_geography";
  const showRouteKmOverride =
    needsReview && result?.reason === "route_distance_unavailable";
  const showDepotKmEditors =
    result?.reason !== "shared_route_missing" &&
    (result?.pricingProduct === "interprovince_shared" ||
      result?.estimate?.pricingProduct === "interprovince_shared" ||
      result?.reason === "depot_legs_unavailable" ||
      result?.originDepotToPickupKm != null ||
      result?.destDepotToDropKm != null ||
      result?.suggestedCollectionKm != null ||
      result?.suggestedDeliveryKm != null ||
      (Array.isArray(result?.reviewReasons) &&
        result.reviewReasons.some((r) =>
          ["COLLECTION_KM_MISSING", "DELIVERY_KM_MISSING"].includes(
            typeof r === "string" ? r : r?.code
          )
        )));
  const suggestedRouteKm = Number(result?.suggestedRouteKm);
  const hasSuggestedRouteKm =
    showRouteKmOverride &&
    Number.isFinite(suggestedRouteKm) &&
    suggestedRouteKm > 0;
  const suggestedRouteKmSource = String(
    result?.suggestedRouteKmSource || ""
  ).trim();
  const originHubLabel = String(
    result?.originDepotHub?.label ||
      result?.estimate?.originDepotHub?.label ||
      ""
  ).trim();
  const destHubLabel = String(
    result?.destDepotHub?.label || result?.estimate?.destDepotHub?.label || ""
  ).trim();
  const collectionSuggested =
    result?.collectionKmSource === "hub_haversine" ||
    Number.isFinite(Number(result?.suggestedCollectionKm));
  const deliverySuggested =
    result?.deliveryKmSource === "hub_haversine" ||
    Number.isFinite(Number(result?.suggestedDeliveryKm));

  const handleSaveRouteKmAndReestimate = useCallback(async () => {
    const kmNum = Number(routeKmOverride);
    if (!Number.isFinite(kmNum) || kmNum <= 0) {
      setError("Enter a positive route distance in km.");
      return;
    }
    await runEstimate({ forceRefresh: true, routeKm: kmNum });
  }, [routeKmOverride, runEstimate]);

  const handleSaveDepotKmAndReestimate = useCallback(async () => {
    const collectionNum = Number(collectionKmOverride);
    const deliveryNum = Number(deliveryKmOverride);
    if (!Number.isFinite(collectionNum) || collectionNum < 0) {
      setError("Enter collection km (hub → pickup, ≥ 0).");
      return;
    }
    if (!Number.isFinite(deliveryNum) || deliveryNum < 0) {
      setError("Enter delivery km (hub → drop, ≥ 0).");
      return;
    }
    await runEstimate({
      forceRefresh: true,
      collectionKm: collectionNum,
      deliveryKm: deliveryNum,
    });
  }, [collectionKmOverride, deliveryKmOverride, runEstimate]);

  const renderSideCandidates = (side, selectedId, setSelectedId, province, setProvince) => {
    const sideData = resolveData?.[side];
    if (!sideData) return null;
    const candidates = Array.isArray(sideData.candidates) ? sideData.candidates : [];
    const label = side === "pickup" ? "Pickup" : "Delivery";

    return (
      <Box
        key={side}
        sx={{ mb: 2 }}
        data-testid={`estimate-resolve-${side}`}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Current: {sideData.input?.address || "—"}
          {sideData.input?.currentProvince
            ? ` (${sideData.input.currentProvince})`
            : ""}
        </Typography>
        {candidates.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            No candidates ({sideData.status}). Pick a province below.
          </Alert>
        ) : (
          <RadioGroup
            value={selectedId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedId(id);
              const c = candidates.find((row) => row.id === id);
              if (c?.provinceCode) setProvince(c.provinceCode);
            }}
          >
            {candidates.map((c) => (
              <FormControlLabel
                key={c.id}
                value={c.id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {c.formattedAddress}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.provinceCode || "?"} · {c.source || "google"}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        )}
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel id={`estimate-${side}-province`}>Province</InputLabel>
          <Select
            labelId={`estimate-${side}-province`}
            label="Province"
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            data-testid={`estimate-resolve-${side}-province`}
          >
            {SA_PROVINCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <>
      <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 480 }, p: 0 },
        "data-testid": "estimate-pricing-panel",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Estimate pricing
        </Typography>
        <IconButton
          aria-label="Close estimate pricing"
          onClick={onClose}
          data-testid="estimate-pricing-close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {result?.estimate?.pricingArchitecture ===
          "adr-008-provider-methodology"
            ? "Live rate card methodology from the linked lead. Does not save or change the quote total."
            : "Decision support from the linked lead. Does not save or change the quote total — close and reopen anytime."}
        </Typography>

        {!leadId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select a lead on this quote form before estimating.
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="estimate-pricing-error">
            {error}
          </Alert>
        )}

        {!loading && needsReview && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-testid="estimate-pricing-needs-review"
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {result?.reason === "shared_route_missing"
                ? `No shared-load tariff for ${
                    result?.geography?.originPricingRegion ||
                    result?.geography?.originProvince ||
                    "origin"
                  } → ${
                    result?.geography?.destinationPricingRegion ||
                    result?.geography?.destinationProvince ||
                    "destination"
                  }`
                : result?.reason === "unresolved_geography" ||
              result?.reason === "incomplete_estimate" ||
              result?.reason === "unresolved_interprovince_service" ||
              result?.reason === "route_distance_unavailable" ||
              result?.reason === "depot_legs_unavailable"
                ? "Review before estimating"
                : "Review items before estimating"}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {result?.message ||
                "Lead items are missing from the quote. Fix the inventory, then try again."}
            </Typography>
            {Array.isArray(result?.missingItems) && result.missingItems.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {result.missingItems.map((item) => (
                  <Typography key={item} component="li" variant="body2">
                    {item}
                  </Typography>
                ))}
              </Box>
            )}
            {result?.reason === "shared_route_missing" && (
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setTariffDrawerOpen(true)}
                data-testid="estimate-create-shared-tariff"
              >
                Suggest &amp; create tariff
              </Button>
            )}
          </Alert>
        )}

        {!loading &&
          needsReview &&
          breakdown &&
          (breakdown.context.length > 0 || breakdown.lines.length > 0) && (
          <Box
            sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: "action.hover" }}
            data-testid="estimate-pricing-breakdown"
          >
            <Typography variant="caption" fontWeight={700} display="block" mb={0.75}>
              Breakdown
            </Typography>
            <Box data-testid="estimate-pricing-breakdown-details">
              {breakdown.context.map((row) => (
                <Typography
                  key={row.key}
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {row.label}: {row.value}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {!loading && showRouteKmOverride && (
          <Box sx={{ mb: 2 }} data-testid="estimate-route-km-override">
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Route distance (km)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Enter a positive km (from maps or ops knowledge). It is saved on the
              lead and used for volume vs mileage. Do not invent a value without a
              real route.
            </Typography>
            {hasSuggestedRouteKm && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
                data-testid="estimate-route-km-suggestion"
              >
                Suggested: {suggestedRouteKm} km
                {suggestedRouteKmSource
                  ? ` (${suggestedRouteKmSource})`
                  : ""}{" "}
                — confirm or edit
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="Route distance (km)"
                value={routeKmOverride}
                onChange={(e) => setRouteKmOverride(e.target.value)}
                inputProps={{ inputMode: "decimal", "data-testid": "estimate-route-km-input" }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={loading || !leadId}
                onClick={handleSaveRouteKmAndReestimate}
                data-testid="estimate-route-km-save"
                sx={{ whiteSpace: "nowrap", mt: 0.5 }}
              >
                Save &amp; re-estimate
              </Button>
            </Stack>
          </Box>
        )}

        {!loading && showDepotKmEditors && (
          <Box sx={{ mb: 2 }} data-testid="estimate-depot-km-editors">
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Collection &amp; delivery km
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Defaults are one-way km from the province city-centre hub
              {originHubLabel || destHubLabel
                ? ` (${[originHubLabel, destHubLabel].filter(Boolean).join(" → ")})`
                : ""}
              . Suggestions are used for this estimate when available — edit if
              needed, then Save &amp; re-estimate to persist on the lead.
            </Typography>
            {(collectionSuggested || deliverySuggested) && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
                data-testid="estimate-depot-km-suggestion"
              >
                Suggested from city-centre hub — edit if needed
              </Typography>
            )}
            <Stack spacing={1.25}>
              <TextField
                size="small"
                fullWidth
                label="Collection km (hub → pickup)"
                value={collectionKmOverride}
                onChange={(e) => setCollectionKmOverride(e.target.value)}
                inputProps={{
                  inputMode: "decimal",
                  "data-testid": "estimate-collection-km-input",
                }}
              />
              <TextField
                size="small"
                fullWidth
                label="Delivery km (hub → drop)"
                value={deliveryKmOverride}
                onChange={(e) => setDeliveryKmOverride(e.target.value)}
                inputProps={{
                  inputMode: "decimal",
                  "data-testid": "estimate-delivery-km-input",
                }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={loading || !leadId}
                onClick={handleSaveDepotKmAndReestimate}
                data-testid="estimate-depot-km-save"
                sx={{ alignSelf: "flex-start" }}
              >
                Save &amp; re-estimate
              </Button>
            </Stack>
          </Box>
        )}

        {!loading && showGeographyResolve && (
          <Box sx={{ mb: 2 }} data-testid="estimate-resolve-addresses">
            {!resolveData && (
              <Button
                variant="outlined"
                size="small"
                disabled={resolving || !leadId}
                onClick={handleResolveAddresses}
                data-testid="estimate-resolve-open"
              >
                {resolving ? "Resolving…" : "Resolve addresses"}
              </Button>
            )}
            {resolveData && (
              <Box
                sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}
                data-testid="estimate-resolve-candidates"
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Choose addresses (saved to lead)
                </Typography>
                {renderSideCandidates(
                  "pickup",
                  pickupCandidateId,
                  setPickupCandidateId,
                  pickupProvince,
                  setPickupProvince
                )}
                {renderSideCandidates(
                  "delivery",
                  deliveryCandidateId,
                  setDeliveryCandidateId,
                  deliveryProvince,
                  setDeliveryProvince
                )}
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="small"
                    disabled={applying}
                    onClick={handleApplyAndReestimate}
                    data-testid="estimate-resolve-apply"
                  >
                    {applying ? "Applying…" : "Apply to lead & re-estimate"}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    disabled={resolving}
                    onClick={handleResolveAddresses}
                  >
                    Re-resolve
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {!loading && result?.status === "ok" && (
          <Stack spacing={1.5} data-testid="estimate-pricing-result">
            <Alert
              severity={suggestedTotal != null ? "success" : "info"}
              data-testid="estimate-pricing-banner"
            >
              <Typography variant="body2" fontWeight={600}>
                Estimated total (not on quote)
              </Typography>
              <Typography variant="h5" component="p" sx={{ my: 0.5 }}>
                {suggestedTotal != null
                  ? formatQuoteMoney(suggestedTotal)
                  : "Incomplete — check rate card / volume"}
              </Typography>
              {result.cached && (
                <Typography
                  variant="caption"
                  display="block"
                  data-testid="estimate-pricing-cached"
                >
                  Cached for this quote — Refresh to recompute
                </Typography>
              )}
              {result.pricingProduct && (
                <Typography variant="caption" display="block">
                  Product: {result.pricingProduct}
                </Typography>
              )}
              {result.volumeSource && (
                <Typography variant="caption" display="block">
                  Volume source: {result.volumeSource}
                </Typography>
              )}
            </Alert>

            {breakdown && (breakdown.context.length > 0 || breakdown.lines.length > 0) && (
              <Box
                sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}
                data-testid="estimate-pricing-breakdown"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: breakdownExpanded ? 0.75 : 0 }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    Breakdown
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={
                      breakdownExpanded
                        ? "Collapse pricing breakdown"
                        : "Expand pricing breakdown"
                    }
                    aria-expanded={breakdownExpanded}
                    onClick={() => setBreakdownExpanded((open) => !open)}
                    data-testid="estimate-pricing-breakdown-toggle"
                  >
                    {breakdownExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Stack>
                <Collapse in={breakdownExpanded} unmountOnExit>
                  <Box data-testid="estimate-pricing-breakdown-details">
                    {breakdown.context.map((row) => (
                      <Typography
                        key={row.key}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {row.label}: {row.value}
                      </Typography>
                    ))}
                    {(breakdown.transportTotal != null ||
                      breakdown.extrasTotal != null) && (
                      <Stack direction="row" gap={2} flexWrap="wrap" mt={1}>
                        {breakdown.transportTotal != null && (
                          <Typography variant="caption" fontWeight={600}>
                            Transport:{" "}
                            {formatQuoteMoney(breakdown.transportTotal)}
                          </Typography>
                        )}
                        {breakdown.extrasTotal != null && (
                          <Typography variant="caption" fontWeight={600}>
                            Extras: {formatQuoteMoney(breakdown.extrasTotal)}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {breakdown.lines.length > 0 && (
                      <Stack
                        spacing={0.25}
                        component="ul"
                        sx={{ m: 0, mt: 1, pl: 2 }}
                      >
                        {breakdown.lines.map((line) => (
                          <Typography
                            key={line.key}
                            component="li"
                            variant="caption"
                          >
                            {line.label}
                            {line.amount != null
                              ? `: ${formatQuoteMoney(line.amount)}`
                              : ""}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            {Array.isArray(result.estimate?.reviewReasons) &&
              result.estimate.reviewReasons.length > 0 && (
                <Alert severity="info" variant="outlined">
                  Engine notes:{" "}
                  {result.estimate.reviewReasons
                    .map((r) => r?.code || r)
                    .filter(Boolean)
                    .join(", ")}
                </Alert>
              )}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            disabled={loading || !leadId}
            onClick={() => runEstimate({ forceRefresh: true })}
            data-testid="estimate-pricing-refresh"
          >
            {loading ? "Estimating…" : "Refresh estimate"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            data-testid="estimate-pricing-done"
          >
            Close
          </Button>
        </Stack>
      </Box>
      </Drawer>
      <SharedRouteTariffDrawer
        open={tariffDrawerOpen}
        onClose={() => setTariffDrawerOpen(false)}
        estimateResult={result}
        onCreated={() => runEstimate({ forceRefresh: true })}
      />
    </>
  );
};

export default EstimatePricingPanel;
